/**
 * Meta "Connect via System User" API client.
 *
 * Thin fetch wrapper matching `integrations-api.ts` / `control-center-api.ts`
 * conventions. RTK Query is intentionally NOT used — kept consistent with the
 * rest of the agency-portal API surface until the (deferred) unify.
 *
 * Auth header: `Authorization: Bearer ${state.auth.agency.accessToken}`.
 * The agency token lives in Redux ONLY (never localStorage).
 *
 * Backend contract (built in parallel):
 *   POST /api/v1/shops/{slug}/integrations/meta/system-user/validate
 *        body { token }
 *        -> { valid, ad_accounts: [{ id, name, currency, account_status }],
 *             business?, error? }
 *   POST /api/v1/shops/{slug}/integrations/meta/system-user/connect
 *        body { token, ad_account_id }
 *        -> { connected, ad_account, token_kind }
 *   GET  /api/v1/shops/{slug}/integrations/meta/status
 *        -> { token_kind: 'system_user'|'user'|null, ad_account_id, connected_at }
 *
 * SECURITY: the system-user token is sent ONLY in the request body of validate /
 * connect. It is never persisted client-side and never logged — the error
 * handlers below deliberately log the parsed server message, never the request.
 *
 * All responses may arrive either bare or `{ data: ... }`-wrapped; `unwrap()`
 * normalises both shapes.
 */
import { CENTRAL_API_BASE_URL } from '../../constants/central-api';
import store from '../../../store/store';

// ---------- shared types ----------

/** A Meta token's provenance. `null` when nothing is connected yet. */
export type MetaTokenKind = 'system_user' | 'user' | null;

/** An ad account returned by the validate step. */
export interface MetaAdAccount {
  id: string;
  name: string;
  currency: string;
  /** Meta's numeric/string account status (1 = active). */
  account_status: number | string;
}

/** Response of POST …/system-user/validate. */
export interface ValidateSystemTokenResponse {
  valid: boolean;
  ad_accounts: MetaAdAccount[];
  /** Optional business the token belongs to (name/id). */
  business?: { id?: string; name?: string } | null;
  /** Present when `valid === false` — a human-readable reason. */
  error?: string | null;
}

/** Response of POST …/system-user/connect. */
export interface ConnectSystemTokenResponse {
  connected: boolean;
  ad_account: MetaAdAccount;
  token_kind: MetaTokenKind;
}

/** Response of GET …/meta/status. */
export interface MetaStatusResponse {
  token_kind: MetaTokenKind;
  ad_account_id: string | null;
  connected_at: string | null;
  /** Optional friendly account name if the backend resolves it. */
  ad_account_name?: string | null;
}

// ---------- internal helpers ----------

function getAuthHeader(token?: string): Record<string, string> {
  const t = token ?? store.getState().auth.agency.accessToken;
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (t) {
    headers['Authorization'] = `Bearer ${t}`;
  }
  return headers;
}

async function parseJson<T>(response: Response): Promise<T> {
  const text = await response.text();
  if (!text) {
    return {} as T;
  }
  try {
    return JSON.parse(text) as T;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[meta-system-user-api] failed to parse JSON', err, text);
    throw new Error('Invalid response from server.');
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  tokenOverride?: string
): Promise<T> {
  const headers: Record<string, string> = {
    ...getAuthHeader(tokenOverride),
    ...((init.headers as Record<string, string>) || {}),
  };

  if (init.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${CENTRAL_API_BASE_URL}${path}`, {
    ...init,
    headers,
  });

  if (!response.ok) {
    let detail = '';
    try {
      const body = await parseJson<{ message?: string; error?: string }>(
        response
      );
      detail = body?.message || body?.error || '';
    } catch {
      // swallow — fall back to status text. NB: we never echo the request body
      // (which carries the system-user token) into logs or thrown errors.
    }
    const err = new Error(
      detail || `Request failed (${response.status} ${response.statusText})`
    ) as Error & { status?: number };
    err.status = response.status;
    throw err;
  }

  return parseJson<T>(response);
}

/** Unwrap a `{ data }` envelope when present; pass through a bare payload. */
function unwrap<T>(raw: T | { data?: T }): T {
  if (
    raw &&
    typeof raw === 'object' &&
    'data' in (raw as Record<string, unknown>) &&
    (raw as { data?: T }).data !== undefined
  ) {
    return (raw as { data: T }).data;
  }
  return raw as T;
}

function basePath(slug: string): string {
  return `shops/${encodeURIComponent(slug)}/integrations/meta`;
}

// ---------- public API ----------

/**
 * Validate a system-user token and fetch the ad accounts it can reach.
 *
 * The token travels ONLY in the request body. On success the caller renders
 * `ad_accounts` for the user to pick the one that is THIS client. When the
 * backend returns `valid: false` the caller surfaces `error` inline.
 */
export async function validateSystemToken(
  slug: string,
  token: string,
  authToken?: string
): Promise<ValidateSystemTokenResponse> {
  const raw = await request<
    ValidateSystemTokenResponse | { data?: ValidateSystemTokenResponse }
  >(
    `${basePath(slug)}/system-user/validate`,
    {
      method: 'POST',
      body: JSON.stringify({ token }),
    },
    authToken
  );
  const body = unwrap<ValidateSystemTokenResponse>(raw);
  return {
    valid: Boolean(body?.valid),
    ad_accounts: Array.isArray(body?.ad_accounts) ? body.ad_accounts : [],
    business: body?.business ?? null,
    error: body?.error ?? null,
  };
}

/**
 * Map the validated system-user token to a chosen ad account and persist the
 * connection server-side. The token is sent again in the body (the FE never
 * stores it between steps beyond the in-memory popup state).
 */
export async function connectSystemToken(
  slug: string,
  token: string,
  adAccountId: string,
  authToken?: string
): Promise<ConnectSystemTokenResponse> {
  const raw = await request<
    ConnectSystemTokenResponse | { data?: ConnectSystemTokenResponse }
  >(
    `${basePath(slug)}/system-user/connect`,
    {
      method: 'POST',
      body: JSON.stringify({ token, ad_account_id: adAccountId }),
    },
    authToken
  );
  return unwrap<ConnectSystemTokenResponse>(raw);
}

/**
 * Read the current Meta integration status for a shop — which token kind is in
 * use (system_user / user / none), the mapped ad account, and when it was
 * connected. Drives the connected-state display and the OAuth nudge.
 */
export async function getMetaStatus(
  slug: string,
  authToken?: string
): Promise<MetaStatusResponse> {
  const raw = await request<MetaStatusResponse | { data?: MetaStatusResponse }>(
    `${basePath(slug)}/status`,
    { method: 'GET' },
    authToken
  );
  const body = unwrap<MetaStatusResponse>(raw);
  return {
    token_kind: body?.token_kind ?? null,
    ad_account_id: body?.ad_account_id ?? null,
    connected_at: body?.connected_at ?? null,
    ad_account_name: body?.ad_account_name ?? null,
  };
}
